import { useContext, useEffect, useRef, useState } from "react";
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import WebApp from "@twa-dev/sdk";

import AuthContext from "../context/auth.context";
import useHttp from "../hooks/http.hook";

/** One network request per channel while in flight (Strict Mode / re-renders). */
const anotherAuthInflight = new Map();

function getTelegramInitData() {
  try {
    return String(WebApp.initData || window.Telegram?.WebApp?.initData || "");
  } catch (_) {
    return "";
  }
}

function AuthError({ message, onRetry }) {
  return (
    <div className="app-authError">
      <p>{message}</p>
      <button type="button" className="app-authErrorButton" onClick={onRetry}>
        Повторить
      </button>
    </div>
  );
}

export function TgChannelHomeRedirect() {
  const { session } = useOutletContext();
  const { tgChannelId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (session?.status !== "ready" || session.branchId == null || !tgChannelId) {
      return;
    }
    navigate(
      `/operator/${encodeURIComponent(tgChannelId)}/branch/${encodeURIComponent(session.branchId)}`,
      { replace: true }
    );
  }, [session?.status, session?.branchId, tgChannelId, navigate]);

  return null;
}

function TgChannelLayout({ authReady }) {
  const { tgChannelId } = useParams();
  const { login } = useContext(AuthContext);
  const { request } = useHttp();

  const [session, setSession] = useState({
    status: "pending",
    branchId: null,
    errorMessage: null,
  });
  const [authRetryCount, setAuthRetryCount] = useState(0);

  const requestRef = useRef(request);
  const loginRef = useRef(login);
  requestRef.current = request;
  loginRef.current = login;

  useEffect(() => {
    if (!authReady || !tgChannelId) {
      return;
    }

    const ch = String(tgChannelId);
    try {
      WebApp.ready();
    } catch (_) {
      // initData may still be available without ready().
    }
    const initDataRaw = getTelegramInitData();
    // const initDataRaw = "user=%7B%22id%22%3A5218704864%2C%22first_name%22%3A%22Omurzak%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22omiiaow%22%2C%22language_code%22%3A%22ru%22%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2F1kf_OO8kFE5uCDdEsXnPZDp3EcFCwcxqT5wn-f2jO7v_pu8xH167Ya2uqgfFwxZN.svg%22%7D&chat_instance=6479956538417833485&chat_type=sender&auth_date=1763273908&signature=L5LjW8IA89bwjbBPterMBA85PNEAU3i_IhDx8wQvCEhNr4-CP2V3uHlJfb6oaTwl43np76MNjQ9kzHrSRWS4Ag&hash=c0abcdc3adbb3b238303a17fa5d01fc3a8f54ebec12a4ea70aa64629e11ee57e";

    setSession({ status: "pending", branchId: null, errorMessage: null });

    if (!initDataRaw) {
      setSession({
        status: "error",
        branchId: null,
        errorMessage:
          "Не удалось получить данные Telegram. Откройте приложение через бот.",
      });
      return;
    }

    let promise = anotherAuthInflight.get(ch);
    if (!promise) {
      promise = requestRef
        .current(
          `/operator/another-auth/${encodeURIComponent(ch)}`,
          "POST",
          { initDataRaw }
        )
        .finally(() => {
          anotherAuthInflight.delete(ch);
        });
      anotherAuthInflight.set(ch, promise);
    }

    let cancelled = false;

    promise
      .then((data) => {
        if (cancelled) return;
        if (!data?.token) {
          setSession({
            status: "error",
            branchId: null,
            errorMessage: "Не удалось войти. Откройте приложение из ссылки заведения.",
          });
          return;
        }
        loginRef.current(data.token);
        setSession({
          status: "ready",
          branchId: data.branchId,
          errorMessage: null,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setSession({
            status: "error",
            branchId: null,
            errorMessage: "Не удалось войти. Откройте приложение из ссылки заведения.",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authReady, tgChannelId, authRetryCount]);

  const retryAuth = () => {
    anotherAuthInflight.delete(String(tgChannelId || ""));
    setSession({ status: "pending", branchId: null, errorMessage: null });
    setAuthRetryCount((count) => count + 1);
  };

  if (session.status === "error") {
    return (
      <AuthError
        message={
          session.errorMessage ||
          "Не удалось войти. Откройте приложение из ссылки заведения."
        }
        onRetry={retryAuth}
      />
    );
  }

  if (session.status !== "ready") {
    return (
      <div className="app-spinnerWrap">
          <svg width="56" height="56" viewBox="0 0 50 50" aria-label="Loading" role="status">
              <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray="31.415, 31.415"
              >
                  <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 25 25"
                      to="360 25 25"
                      dur="1s"
                      repeatCount="indefinite"
                  />
              </circle>
          </svg>
      </div>
    );
  }

  return <Outlet context={{ session }} />;
}

export default TgChannelLayout;
